'use client';

import { useEffect, useRef, useState } from 'react';

export interface VideoLoopProps {
  src: string;
  alt: string;
  poster?: string;
  caption?: string;
  /** Width / height of the clip; sizes the frame before the first frame arrives. */
  aspect?: number;
}

/**
 * Self-hosted clip that autoplays muted and loops: an animated figure, not a player.
 *
 * The file is not requested until the frame is within 400px of the viewport, so a guide with
 * several clips only downloads the one the reader is near. Readers who ask for reduced motion get
 * the poster with normal controls instead of an autoplaying loop.
 */
export const VideoLoop = ({ src, alt, poster, caption, aspect = 16 / 9 }: VideoLoopProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          setNear(true);
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <figure className="my-8">
      <div
        ref={frameRef}
        className="relative w-full overflow-hidden rounded-lg border border-zinc-700 bg-[#141414]"
        style={{ aspectRatio: String(aspect) }}
      >
        {near ? (
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src={src}
            poster={poster}
            autoPlay={!reduceMotion}
            loop
            muted
            playsInline
            controls={reduceMotion}
            preload={reduceMotion ? 'metadata' : 'auto'}
            aria-label={alt}
            disablePictureInPicture
          >
            {alt}
          </video>
        ) : poster ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={poster} alt={alt} className="absolute inset-0 h-full w-full object-cover" />
        ) : null}
      </div>
      {caption && <figcaption className="mt-3 text-sm text-zinc-400">{caption}</figcaption>}
    </figure>
  );
};
